import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdjustInventoryInput, AvailabilityResponse, BrandListResponse, CreateBrandInput, CreateInventoryItemInput, InventoryItem,
  InventoryMovement, UpdateBrandInput, UpdateInventoryItemInput,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useInventory(workspaceId: string | null, params: { query?: string; status?: string; category?: string; brand?: string }) {
  return useQuery({
    queryKey: [workspaceId, 'inventory', params],
    queryFn: ({ signal }) => api.get<{ items: InventoryItem[]; nextCursor: string | null }>(workspacePath(workspaceId!, '/inventory'), { query: { ...params, limit: 100 }, signal }),
    enabled: !!workspaceId,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
}

export function useInventoryAvailability(workspaceId: string | null, materialIds: string[]) {
  const key = materialIds.slice().sort().join(',');
  return useQuery({
    queryKey: [workspaceId, 'inventory', 'availability', key],
    queryFn: () => api.get<AvailabilityResponse>(workspacePath(workspaceId!, '/inventory/availability'), { query: { materialIds: key } }),
    enabled: !!workspaceId && materialIds.length > 0,
    staleTime: 10_000,
  });
}

// Single-item detail - lets MaterialPicker (quotes/pricing) read/edit a
// stocked item's priceCentsPerUnit inline without pulling the whole list.
export function useInventoryItem(workspaceId: string | null, itemId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'inventory', itemId],
    queryFn: () => api.get<InventoryItem>(workspacePath(workspaceId!, `/inventory/${itemId}`)),
    enabled: !!workspaceId && !!itemId,
    staleTime: 10_000,
  });
}

export function useInventoryMovements(workspaceId: string | null, itemId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'inventory', itemId, 'movements'],
    queryFn: () => api.get<{ items: InventoryMovement[]; nextCursor: string | null }>(workspacePath(workspaceId!, `/inventory/${itemId}/movements`)),
    enabled: !!workspaceId && !!itemId,
  });
}

function useInvalidateInventory(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'inventory'] });
}

export function useCreateInventoryItem(workspaceId: string | null) {
  const invalidate = useInvalidateInventory(workspaceId);
  return useMutation({
    mutationFn: (input: CreateInventoryItemInput) => api.post<{ id: string }>(workspacePath(workspaceId!, '/inventory'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateInventoryItem(workspaceId: string | null) {
  const invalidate = useInvalidateInventory(workspaceId);
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdateInventoryItemInput }) => api.patch(workspacePath(workspaceId!, `/inventory/${itemId}`), input),
    onSuccess: invalidate,
  });
}

export function useAdjustInventory(workspaceId: string | null) {
  const invalidate = useInvalidateInventory(workspaceId);
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: AdjustInventoryInput }) => api.post(workspacePath(workspaceId!, `/inventory/${itemId}/adjust`), input),
    onSuccess: invalidate,
  });
}

export function useBrands(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'brands'],
    queryFn: () => api.get<BrandListResponse>(workspacePath(workspaceId!, '/brands')),
    enabled: !!workspaceId,
    staleTime: 10_000,
  });
}

function useInvalidateBrands(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'brands'] });
}

export function useCreateBrand(workspaceId: string | null) {
  const invalidate = useInvalidateBrands(workspaceId);
  return useMutation({
    mutationFn: (input: CreateBrandInput) => api.post<{ id: string }>(workspacePath(workspaceId!, '/brands'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateBrand(workspaceId: string | null) {
  const invalidate = useInvalidateBrands(workspaceId);
  const invalidateItems = useInvalidateInventory(workspaceId);
  return useMutation({
    mutationFn: ({ brandId, input }: { brandId: string; input: UpdateBrandInput }) => api.patch(workspacePath(workspaceId!, `/brands/${brandId}`), input),
    onSuccess: () => {
      invalidate();
      invalidateItems();
    },
  });
}

export function useDeleteBrand(workspaceId: string | null) {
  const invalidate = useInvalidateBrands(workspaceId);
  const invalidateItems = useInvalidateInventory(workspaceId);
  return useMutation({
    mutationFn: (brandId: string) => api.delete(workspacePath(workspaceId!, `/brands/${brandId}`)),
    onSuccess: () => {
      invalidate();
      invalidateItems();
    },
  });
}
